import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Backdrop,
  CircularProgress,
} from '@mui/material';

import LandingToolBar from '../../Main/Landing/LangingToolBar';
import StorageFactory from '../../Util/StorageFactory';
import { SESSION_USER_ID_KEY } from '../../Util/VMConstants';
import { isEmpty, toTitleCase } from '../../Util/UtilHelper';
import MUITypography from '../../Components/MUITypography';
import SocialMediaIcons from '../../Components/SocialMediaIcons';
import FooterSection from '../../Main/Landing/FooterSection';
import { API_SUBMIT_CONTACT } from '../../Util/URLConstants';

function VMLandingContactUs() {
  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_no: '',
    message: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (field) => (e) => {
    let value = e.target.value;
    if (field === 'phone_no') {
      value = value.replace(/\D/g, '').slice(0, 10);
    }
    setForm({ ...form, [field]: value });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async () => {
    if (
      isEmpty(form.first_name) ||
      isEmpty(form.last_name) ||
      isEmpty(form.email) ||
      isEmpty(form.phone_no) ||
      isEmpty(form.message)
    ) {
      setError('All fields are required');
      return;
    }

    if (!/^\d{10}$/.test(form.phone_no.trim())) {
      setError('Enter a valid 10-digit mobile number');
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(( API_SUBMIT_CONTACT), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const result = await response.json();

      if (result.status_code === 200) {
        setSuccess('Message sent successfully');
        setForm({
          first_name: '',
          last_name: '',
          email: '',
          phone_no: '',
          message: '',
        });
      } else {
        setError(result?.data?.message || 'Something went wrong');
      }
    } catch (err) {
      setError('Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      {isEmpty(userID) && <LandingToolBar />}

      <Box
        px={{ xs: 2, sm: 4, md: 8 }}
        py={{ xs: 4, md: 8 }}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 6,
          alignItems: 'center',
        }}
      >
        {/* LEFT CONTENT */}
        <Box>
          <MUITypography variant="h1" fontWeight={700} mb={2}>
            Get in Touch with us
          </MUITypography>

          <MUITypography color="text.secondary" mb={4}>
            {toTitleCase('We’re here to help! Whether you have a question about our services, need assistance with your account, or want to provide feedback,  our team is ready to assist you.')}
          </MUITypography>

          <MUITypography fontWeight={600}>Company:</MUITypography>
          <MUITypography sx={{ color: '#475569', mb: 3 }}>
            GAAMI WORKS CONSULTING PRIVATE LIMITED
          </MUITypography>

          <MUITypography fontWeight={600}>Address:</MUITypography>
          <MUITypography sx={{ color: '#475569', lineHeight: 1.7, mb: 3 }}>
            402, 7-1-50, Siddharatha Apartments,
            <br />
            Dharam Karam Road,
            <br />
            Hyderabad, Telangana 500038,
            <br />
            India
          </MUITypography>

          <MUITypography fontWeight={600}>Email:</MUITypography>
          <MUITypography
            component="a"
            href="mailto:info@venuemanagercrm.com"
            sx={{ textDecoration: 'none', color: '#475569' }}
          >
            info@venuemanagercrm.com
          </MUITypography>

          <MUITypography fontWeight={600} mt={3}>
            Phone:
          </MUITypography>
          <MUITypography
            component="a"
            href="tel:+918897545660"
            fontWeight={700}
            sx={{ textDecoration: 'none', color: 'inherit' }}
          >
            +91 88975 45660
          </MUITypography>

          <MUITypography fontWeight={600} mt={3} mb={1}>
            Follow Us
          </MUITypography>
          <SocialMediaIcons brandColors />
        </Box>

        {/* RIGHT FORM */}
        <Card sx={{ borderRadius: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
                mb: 3,
              }}
            >
              <TextField
                label="First Name"
                size="small"
                value={form.first_name}
                onChange={handleChange('first_name')}
                fullWidth
                disabled={loading}
              />

              <TextField
                label="Last Name"
                size="small"
                value={form.last_name}
                onChange={handleChange('last_name')}
                fullWidth
                disabled={loading}
              />
            </Box>

            <Box mb={3}>
              <TextField
                label="Email"
                size="small"
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                fullWidth
                disabled={loading}
              />
            </Box>

            <Box mb={3}>
              <TextField
                label="Mobile number"
                size="small"
                value={form.phone_no}
                onChange={handleChange('phone_no')}
                fullWidth
                disabled={loading}
                placeholder="10-digit mobile number"
                inputProps={{ inputMode: 'numeric', maxLength: 10 }}
              />
            </Box>

            <Box mb={3}>
              <TextField
                label="How can I help you?"
                multiline
                rows={4}
                value={form.message}
                onChange={handleChange('message')}
                fullWidth
                disabled={loading}
              />
            </Box>

            {error && (
              <Typography color="error" mb={2}>
                {error}
              </Typography>
            )}

            {success && (
              <Typography color="success.main" mb={2}>
                {success}
              </Typography>
            )}

            <Box textAlign="right">
              <Button
                variant="contained"
                disabled={loading}
                onClick={handleSubmit}
                startIcon={
                  loading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : null
                }
                sx={{
                  backgroundColor: '#253A8F',
                  px: 4,
                  py: 1.2,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                {loading ? 'Sending...' : 'Send Message'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {isEmpty(userID) && <FooterSection />}
    </Box>
  );
}

export default VMLandingContactUs;
